import './AuthPage.css';
import React, { useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { useNavigate } from 'react-router-dom';
import googleIcon from '../../assets/img/google-icon.svg';
import { useAuth } from 'src/provider/AuthProvider';
import { getEnvironment } from 'src/env/env';
import { useEffectOnce } from 'src/hooks/useEffectOnce';
import { parseUrl } from 'src/services/ParseUrl';
import { useNotification } from 'src/hooks/useNotification';
import { LoginForm } from './LoginForm';
import { AuthWrapper } from 'src/components/AuthWrapper/AuthWrapper';
import { persistor, useAppSelector } from 'src/store';
import { selectManagedAccountId } from 'src/store/slices/companySlice';
import { useDispatch } from 'react-redux';
import { baseApi } from 'src/services';
import { setProjectId } from 'src/store/slices/projectsSlices';
import { PATH_HOME, PATH_ONBOARDING, PATH_REGISTER } from 'src/constants/spa-routes';
import { useLoader } from 'src/provider/LoaderProvider';
import { EmailVerificationPopup } from 'src/pages/AuthPage/EmailVerificationPopup/EmailVerificationPopup';
import { ResetPasswordPopup } from 'src/pages/AuthPage/ResetPasswordPopup/ResetPasswordPopup';
import { useRegistrationConfirmMutation } from 'src/services/authSsoService';
import { getAccessToken } from 'src/utils/sse.utils';
import { useGetAccountsQuery } from 'src/services/accountsService';
import useBroadcast from 'src/hooks/useBroadcast';
import { BROADCAST_CHANNELS, BROADCAST_MESSAGES } from 'src/constants/broadcast';
import { ProductsType } from 'src/constants/products';
import { COSUPPORT_URL } from 'src/constants/urls';

const AuthPage = () => {
  const { currentStep } = useAppSelector((s) => s.onboardingSlice);
  const navigate = useNavigate();
  const { signInWithGoogleFunc, user } = useAuth();
  const notification = useNotification();
  const loader = useLoader();
  const dispatch = useDispatch();
  const [emailVerificationPopupOpen, setEmailVerificationPopupOpen] = React.useState(false);
  const [emailVerificationMessage, setEmailVerificationMessage] = React.useState('');
  const [googleSignInError, setGoogleSignInError] = React.useState(false);
  const { channelInstance } = useBroadcast({
    channelName: BROADCAST_CHANNELS.AUTH,
  });
  const { data: account } = useGetAccountsQuery(undefined, { skip: !user });
  const [
    registrationConfirm,
    {
      data: registrationConfirmData,
      isError: isErrorRegistrationConfirm,
      isLoading: isLoadingRegistrationConfirm,
      isSuccess: isSuccessRegistrationConfirm,
    },
  ] = useRegistrationConfirmMutation();

  const loginByGoogle = async () => {
    setGoogleSignInError(false);

    const { APP_REDIRECT_URI, APP_GOOGLE_OAUTH_CLIENT_ID } = await getEnvironment();
    const scope = {
      profile: 'https://www.googleapis.com/auth/userinfo.profile',
      email: 'https://www.googleapis.com/auth/userinfo.email',
    };
    window.location.href = `https://accounts.google.com/o/oauth2/auth?client_id=${APP_GOOGLE_OAUTH_CLIENT_ID}&redirect_uri=${encodeURIComponent(APP_REDIRECT_URI)}&scope=${encodeURIComponent(`${scope.profile} ${scope.email}`)}&response_type=code&include_granted_scopes=true&&access_type=offline&state=state_parameter_passthrough_value&prompt=select_account+consent`;
  };

  useEffectOnce(() => {
    const auth = async () => {
      const queryParams = parseUrl(window.location.href);
      if (queryParams.code) {
        try {
          const response = await signInWithGoogleFunc(queryParams.code);

          if (response instanceof Error) {
            return setGoogleSignInError(true);
            //return response.message && notification.error(response.message);
          }

          if (response.access || response.access_token) {
            return;
          }
        } catch (error) {
          console.error(error);
        }
      }

      if (queryParams.verify) {
        await registrationConfirm({ token: queryParams.verify });
        return;
      }
    };
    auth();
    return () => {};
  });

  useEffect(() => {
    if (isSuccessRegistrationConfirm) {
      loader.hide();
    }
  }, [isSuccessRegistrationConfirm]);

  useEffect(() => {
    if (registrationConfirmData) {
      notification.success((registrationConfirmData as any).message);
      setEmailVerificationMessage((registrationConfirmData as any).message);
      setEmailVerificationPopupOpen(true);
      channelInstance?.postMessage(BROADCAST_MESSAGES.WINDOW_RELOAD);
      loader.hide();
    }
  }, [registrationConfirmData]);

  useEffect(() => {
    if (isLoadingRegistrationConfirm) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingRegistrationConfirm]);

  useEffect(() => {
    if (isErrorRegistrationConfirm) {
      loader.hide();
      setEmailVerificationPopupOpen(true);
    }
  }, [isErrorRegistrationConfirm]);

  useEffect(() => {
    const queryParams = parseUrl(window.location.href);

    if (!queryParams.verify) {
      dispatch(baseApi.util.resetApiState());
      dispatch(selectManagedAccountId(''));
      dispatch(setProjectId({ projectId: null, projectType: null }));
      persistor.flush().then(() => {
        return persistor.purge();
      });
      localStorage.removeItem('COMPANY_ID');
      onEmailVerificationPopupClose();
    }
  }, []);

  useEffect(() => {
    const queryParams = parseUrl(window.location.href);

    if (queryParams.code && account) {
      const company = account.company_name;
      account.version && localStorage.setItem('API_VERSION', account.version);
      navigate(company ? PATH_HOME.ROOT : PATH_REGISTER.GOOGLE);
    }
  }, [account]);

  const onEmailVerificationPopupClose = () => {
    const access = getAccessToken();

    if (access) {
      currentStep
        ? navigate(`${PATH_ONBOARDING.ROOT}/${currentStep || 0}?type=${ProductsType.customer}`)
        : navigate(PATH_HOME.ROOT);
    }
  };

  const onSubmit = () => {
    setGoogleSignInError(false);
  };

  return (
    <AuthWrapper>
      <div
        className={
          'flex flex-col w-[450px] md:w-[534px] rounded-[15px] border border-white bg-[#ffffff66] pt-[25px] px-[46px] pb-10'
        }
      >
        <EmailVerificationPopup
          open={emailVerificationPopupOpen}
          setOpen={setEmailVerificationPopupOpen}
          onClose={onEmailVerificationPopupClose}
          isSuccess={isSuccessRegistrationConfirm}
          isError={isErrorRegistrationConfirm}
          message={emailVerificationMessage}
        />
        {googleSignInError && (
          <div className={'bg-error_bg rounded-sm-5 text-error text-sm p-4 mb-2'}>
            Account doesn’t exist. Please check your email address and password or request access
            using{' '}
            <a
              className={'underline italic text-link'}
              href={COSUPPORT_URL.REQUEST_DEMO}
              target="_blank"
              rel="noreferrer"
            >
              this form
            </a>
          </div>
        )}
        <LoginForm onSubmit={onSubmit} />
        <Button
          variant="outline"
          onClick={loginByGoogle}
          className={
            'flex flex-row gap-2.5 mt-[15px] min-h-[50px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]'
          }
        >
          <img src={googleIcon} width="18px" alt="Google icon" />
          <span>Sign in with Google</span>
        </Button>
        <div className="flex items-center justify-center pt-[15px]">
          <ResetPasswordPopup />
        </div>
        <div className="py-5 text-[#B8C1C6] flex flex-row justify-center overflow-hidden items-center relative">
          <div className={'h-px bg-[#B8C1C6] w-full'}></div>
          <p className="inline-block text-[#B8C1C6] text-sm font-medium text-center relative px-6 text-nowrap">
            New to CoSupport AI?
          </p>
          <div className={'h-px bg-[#B8C1C6] w-full'}></div>
        </div>
        <Button
          variant="green"
          className="min-h-[50px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]"
          // onClick={() => navigate('/register')}
          onClick={() => window.open(COSUPPORT_URL.REQUEST_DEMO, '_self')}
        >
          {/*Create account*/}
          Request Access
        </Button>
      </div>
    </AuthWrapper>
  );
};

export default AuthPage;
